/**
 * useAdminLogs Hook
 * Main hook for managing admin logs state and operations
 * Optimized with server-side pagination support
 */

import { useState, useEffect, useCallback, useMemo, useRef, type Dispatch, type SetStateAction } from 'react';
import {
  AdminLogEntry,
  LogsFilterState,
  LogsPaginationState,
  LogsStats,
  LogsAnalyticsData,
  DEFAULT_FILTERS,
  DEFAULT_PAGINATION,
} from '../types';
import { getAdminLogs, extractUsersFromLogs, exportLogs, downloadExport, enrichLogsWithUserImages, fetchAdminUsersForFilter } from '../api';
import { filterLogs, calculateStats, generateAnalytics } from '../utils';

/** Default lookback when the user has not set a date filter — keep in sync with backend. */
const DEFAULT_LOOKBACK_DAYS = 7;
const INITIAL_PAGE_SIZE = 25;
const LOAD_MORE_PAGE_SIZE = 50;
/** When filtering by user/category, pull larger chunks so we can fill a page of matches. */
const FILTERED_PAGE_SIZE = 100;
/** Keep auto-loading until we have this many matching rows (or pages run out). */
const FILTERED_TARGET_COUNT = 25;
const FILTERED_MAX_AUTO_PAGES = 10;

/** Inclusive lookback: Last 7 days = today + 6 prior calendar days. */
function defaultStartDate(): Date {
  const d = new Date();
  d.setDate(d.getDate() - (DEFAULT_LOOKBACK_DAYS - 1));
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Inclusive end-of-day so "today" is not cut off at midnight. */
function toEndOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

/** Always send ISO-8601 to the API — locale Date strings break GCP Logging. */
function toIsoCursor(value?: string | null): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

type FilterUser = { id: number; name: string; email: string; image?: string | null };

function mergeUsers(base: FilterUser[], extra: FilterUser[]): FilterUser[] {
  const map = new Map<number, FilterUser>();
  base.forEach(u => map.set(u.id, u));
  extra.forEach(u => {
    const existing = map.get(u.id);
    if (!existing) {
      map.set(u.id, u);
      return;
    }
    // Prefer richer name/image from either source
    map.set(u.id, {
      ...existing,
      name: existing.name && existing.name !== 'Unknown' ? existing.name : u.name,
      email: existing.email || u.email,
      image: existing.image || u.image || null,
    });
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/** Fill in user avatars after logs paint — keeps first load fast. */
async function applyUserImages(
  logs: AdminLogEntry[],
  setAllLogs: Dispatch<SetStateAction<AdminLogEntry[]>>,
  setUsers: Dispatch<SetStateAction<FilterUser[]>>,
  mode: 'replace' | 'merge'
): Promise<void> {
  try {
    const enriched = await enrichLogsWithUserImages(logs);
    const hasAnyImage = enriched.some(l => l.user?.image);
    if (!hasAnyImage) return;

    if (mode === 'replace') {
      setAllLogs(enriched);
      setUsers(prev => mergeUsers(prev, extractUsersFromLogs(enriched)));
      return;
    }

    // Merge enriched images into the current list by id (load-more safe).
    const imageById = new Map(
      enriched.filter(l => l.user?.image).map(l => [l.id, l.user.image as string])
    );
    if (imageById.size === 0) return;

    setAllLogs(prev => {
      const next = prev.map(log => {
        const image = imageById.get(log.id);
        if (!image || log.user?.image) return log;
        return { ...log, user: { ...log.user, image } };
      });
      setUsers(prevUsers => mergeUsers(prevUsers, extractUsersFromLogs(next)));
      return next;
    });
  } catch (err) {
    console.warn('Failed to enrich log user images:', err);
  }
}

interface UseAdminLogsOptions {
  autoFetch?: boolean;
  pageSize?: number;
}

interface UseAdminLogsReturn {
  // Data
  logs: AdminLogEntry[];
  allLogs: AdminLogEntry[];
  stats: LogsStats;
  analytics: LogsAnalyticsData | null;
  users: Array<{ id: number; name: string; email: string }>;
  
  // State
  filters: LogsFilterState;
  pagination: LogsPaginationState;
  isLoading: boolean;
  isRefreshing: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  
  // Actions
  setFilters: (filters: Partial<LogsFilterState>) => void;
  resetFilters: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  exportData: (format: 'csv' | 'json', includePayload?: boolean) => Promise<void>;
  
  // Computed
  hasActiveFilters: boolean;
  paginatedLogs: AdminLogEntry[];
  totalPages: number;
}

export function useAdminLogs(options: UseAdminLogsOptions = {}): UseAdminLogsReturn {
  const { autoFetch = true, pageSize = 25 } = options;
  
  // Raw data from API
  const [allLogs, setAllLogs] = useState<AdminLogEntry[]>([]);
  const [users, setUsers] = useState<FilterUser[]>([]);
  
  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Timestamp-based pagination (more reliable than pageToken)
  const [oldestTimestamp, setOldestTimestamp] = useState<string | undefined>(undefined);
  const [hasMoreLogs, setHasMoreLogs] = useState(false);
  
  // Filters
  const [filters, setFiltersState] = useState<LogsFilterState>(DEFAULT_FILTERS);
  
  // Pagination
  const [pagination, setPagination] = useState<LogsPaginationState>({
    ...DEFAULT_PAGINATION,
    pageSize,
  });

  // Track if initial fetch is done
  const initialFetchDone = useRef(false);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  // Shared with fetchLogs reset + auto-fill effect below
  const autoFillAttempts = useRef(0);

  // Full admin list for the user dropdown (not only users on the current log page).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const adminUsers = await fetchAdminUsersForFilter();
      if (!cancelled && adminUsers.length > 0) {
        setUsers(prev => mergeUsers(adminUsers, prev));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Build API params — shared date window only.
   *  Status / user / category / search / errors-only are Activity list filters
   *  applied client-side so Analytics stays on the full loaded set for that range. */
  const buildFetchParams = useCallback((extra: {
    pageSize: number;
    beforeTimestamp?: string;
    refresh?: boolean;
  } = { pageSize: INITIAL_PAGE_SIZE }) => {
    const f = filtersRef.current;
    return {
      pageSize: extra.pageSize,
      beforeTimestamp: toIsoCursor(extra.beforeTimestamp),
      refresh: extra.refresh,
      startDate: f.dateRange.startDate ?? defaultStartDate(),
      // Inclusive end-of-day — date picker often returns midnight which would
      // exclude all of "today" from the GCP query.
      endDate: f.dateRange.endDate ? toEndOfDay(f.dateRange.endDate) : undefined,
    };
  }, []);

  const hasClientListFilter = useCallback(() => {
    const f = filtersRef.current;
    return f.users.length > 0 || f.categories.length > 0;
  }, []);
  
  // Fetch logs - passes date window + filters to shrink the GCP Logging scan.
  // mode:
  //  - initial: first load spinner
  //  - refresh: keep current rows, header refresh spinner
  //  - filter: clear rows + table spinner so we don't flash stale filtered matches
  const fetchLogs = useCallback(async (mode: 'initial' | 'refresh' | 'filter' = 'initial') => {
    try {
      if (mode === 'refresh') {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
        if (mode === 'filter') {
          // Avoid showing 2–3 matches from the previous page while the new fetch runs.
          setAllLogs([]);
        }
      }
      setError(null);
      // Reset pagination cursor so we never reuse a stale/locale timestamp.
      setOldestTimestamp(undefined);
      setHasMoreLogs(false);
      autoFillAttempts.current = 0;
      
      const pageSize = hasClientListFilter() ? FILTERED_PAGE_SIZE : INITIAL_PAGE_SIZE;
      const logsResult = await getAdminLogs(
        buildFetchParams({ pageSize, refresh: true })
      );
      const usersFromLogs = extractUsersFromLogs(logsResult.logs);
      
      // Paint logs immediately, then fill avatars in the background.
      setAllLogs(logsResult.logs);
      setUsers(prev => mergeUsers(prev, usersFromLogs));
      setOldestTimestamp(toIsoCursor(logsResult.oldestTimestamp));
      setHasMoreLogs(logsResult.hasMore);
      setPagination(prev => ({
        ...prev,
        totalItems: logsResult.logs.length,
      }));
      initialFetchDone.current = true;

      void applyUserImages(logsResult.logs, setAllLogs, setUsers, 'replace');
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch logs');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [buildFetchParams, hasClientListFilter]);

  // Load more logs using timestamp-based pagination (same filters/window as initial fetch)
  const loadMore = useCallback(async () => {
    const cursor = toIsoCursor(oldestTimestamp);
    if (!cursor || !hasMoreLogs || isLoadingMore) return;
    
    try {
      setIsLoadingMore(true);
      setError(null);
      
      const pageSize = hasClientListFilter() ? FILTERED_PAGE_SIZE : LOAD_MORE_PAGE_SIZE;
      const logsResult = await getAdminLogs(
        buildFetchParams({ pageSize, beforeTimestamp: cursor, refresh: true })
      );
      
      // Append new logs to existing
      setAllLogs(prev => {
        const newLogs = [...prev, ...logsResult.logs];
        setUsers(prevUsers => mergeUsers(prevUsers, extractUsersFromLogs(newLogs)));
        return newLogs;
      });
      
      // Update pagination state
      setOldestTimestamp(toIsoCursor(logsResult.oldestTimestamp));
      setHasMoreLogs(logsResult.hasMore);
      setPagination(prev => ({
        ...prev,
        totalItems: prev.totalItems + logsResult.logs.length,
      }));

      // Enrich only the newly fetched page, then merge images into the full list.
      void applyUserImages(logsResult.logs, setAllLogs, setUsers, 'merge');
    } catch (err) {
      console.error('Error loading more logs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load more logs');
      // Stop auto-fill loops on hard failures (e.g. bad cursor)
      setHasMoreLogs(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [oldestTimestamp, hasMoreLogs, isLoadingMore, buildFetchParams, hasClientListFilter]);
  
  // Initial fetch
  useEffect(() => {
    if (autoFetch) {
      void fetchLogs('initial');
    }
  }, [autoFetch, fetchLogs]);

  // Refetch only when the shared date window changes.
  // Activity list filters (user / status / category / search) stay client-side
  // so Analytics keeps the full loaded set for that same date range.
  const dateRangeKey = useMemo(
    () =>
      JSON.stringify({
        start: filters.dateRange.startDate?.toISOString() ?? null,
        end: filters.dateRange.endDate?.toISOString() ?? null,
      }),
    [filters.dateRange.startDate, filters.dateRange.endDate]
  );

  const prevDateRangeKey = useRef(dateRangeKey);
  useEffect(() => {
    if (!initialFetchDone.current) {
      prevDateRangeKey.current = dateRangeKey;
      return;
    }
    if (prevDateRangeKey.current === dateRangeKey) return;
    prevDateRangeKey.current = dateRangeKey;
    void fetchLogs('filter');
  }, [dateRangeKey, fetchLogs]);
  
  // Activity table rows (client-side list filters; Analytics uses allLogs)
  const logs = useMemo(() => {
    // While a filter refetch is in flight, allLogs is cleared and isLoading is true —
    // don't flash a partial client-filtered subset of the previous page.
    if (isLoading) return [];
    return filterLogs(allLogs, filters);
  }, [allLogs, filters, isLoading]);

  // After selecting a user/category, keep pulling pages until we have a full
  // page of matches (or run out of data) — not just the 2–3 from the first batch.
  useEffect(() => {
    const needsFill = filters.users.length > 0 || filters.categories.length > 0;
    if (!needsFill) {
      autoFillAttempts.current = 0;
      return;
    }
    if (isLoading || isLoadingMore || isRefreshing) return;
    if (!hasMoreLogs || !oldestTimestamp) {
      autoFillAttempts.current = 0;
      return;
    }
    if (logs.length >= FILTERED_TARGET_COUNT) {
      autoFillAttempts.current = 0;
      return;
    }
    if (autoFillAttempts.current >= FILTERED_MAX_AUTO_PAGES) return;
    autoFillAttempts.current += 1;
    void loadMore();
  }, [
    logs.length,
    filters.users,
    filters.categories,
    hasMoreLogs,
    oldestTimestamp,
    isLoading,
    isLoadingMore,
    isRefreshing,
    loadMore,
  ]);
  
  // Update pagination when filters change
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      pageIndex: 0,
      totalItems: logs.length,
    }));
  }, [logs.length]);
  
  // Stats
  const stats = useMemo(() => {
    return calculateStats(logs);
  }, [logs]);
  
  // Analytics — based on unfiltered loaded logs + shared date window
  const analytics = useMemo(() => {
    if (allLogs.length === 0) return null;
    return generateAnalytics(allLogs, filters.dateRange);
  }, [allLogs, filters.dateRange]);
  
  // Paginated logs
  const paginatedLogs = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return logs.slice(start, end);
  }, [logs, pagination.pageIndex, pagination.pageSize]);
  
  // Total pages
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(logs.length / pagination.pageSize));
  }, [logs.length, pagination.pageSize]);
  
  // List filters only — date range is shared page scope (header), not a "clearable" filter
  const hasActiveFilters = useMemo(() => {
    return (
      filters.searchText !== '' ||
      filters.severity.length > 0 ||
      filters.categories.length > 0 ||
      filters.users.length > 0 ||
      filters.showOnlyErrors
    );
  }, [filters]);
  
  // Set filters
  const setFilters = useCallback((newFilters: Partial<LogsFilterState>) => {
    setFiltersState(prev => ({
      ...prev,
      ...newFilters,
    }));
  }, []);
  
  // Reset filters
  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);
  
  // Set page
  const setPage = useCallback((page: number) => {
    setPagination(prev => ({
      ...prev,
      pageIndex: page,
    }));
  }, []);
  
  // Set page size
  const setPageSize = useCallback((size: number) => {
    setPagination(prev => ({
      ...prev,
      pageSize: size,
      pageIndex: 0,
    }));
  }, []);
  
  // Refresh
  const refresh = useCallback(async () => {
    await fetchLogs('refresh');
  }, [fetchLogs]);
  
  // Export
  const exportData = useCallback(async (format: 'csv' | 'json', includePayload = false) => {
    try {
      const content = await exportLogs({
        format,
        includePayload,
        filters,
        dateRange: filters.dateRange.startDate && filters.dateRange.endDate
          ? {
              startDate: filters.dateRange.startDate,
              endDate: filters.dateRange.endDate,
            }
          : {
              startDate: defaultStartDate(),
              endDate: new Date(),
            },
      });
      
      downloadExport(content, format);
    } catch (err) {
      console.error('Error exporting logs:', err);
      throw err;
    }
  }, [filters]);
  
  // Has more pages available
  const hasMore = hasMoreLogs && !!oldestTimestamp;

  return {
    // Data
    logs,
    allLogs,
    stats,
    analytics,
    users,
    
    // State
    filters,
    pagination,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    hasMore,
    
    // Actions
    setFilters,
    resetFilters,
    setPage,
    setPageSize,
    refresh,
    loadMore,
    exportData,
    
    // Computed
    hasActiveFilters,
    paginatedLogs,
    totalPages,
  };
}
